'use client'

import { Button } from '@brew/ui/button'
import { cn } from '@brew/ui/cn'
import { AlertTriangle, Check, ExternalLink, Loader2 } from 'lucide-react'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { type ReactNode, Suspense } from 'react'
import { useCapabilityGate } from '@/hooks/use-can'
import type {
  IntegrationMeta,
  IntegrationProvider,
} from '@/lib/integrations/catalog'
import { describeStripeConnectError } from '@/lib/integrations/oauth/stripe/connect-errors'
import { ICON } from '@/lib/ui/icon'
import { TYPE } from '@/lib/ui/typography'

/**
 * Shared chrome for the integration detail page's primary CTA.
 *
 * Historically this file owned a sticky right-rail that contained the
 * connect button, a status pill, a `Details` description list (Type /
 * Direction / Category / Pricing / Author), and a `Resources` list of
 * outbound links. The sidebar packed too much filler around the one
 * thing users actually came to do — connect — so the page was
 * restructured into an Attio-style two-column layout (left meta rail
 * + right tabbed content) with the CTA in the hero top-right. The
 * `Details` / `Resources` content now lives inline in `MetaRail`
 * inside `integration-page.tsx`.
 *
 * What remains here is the primitives the new layout still composes:
 *
 *  - `CtaBlock` — the primary connect / disconnect / coming-soon
 *    button. Rendered inline in the hero top-right as the single
 *    connect action for the page. (It is no longer mirrored into the
 *    app-shell header — that duplicated the hero CTA and was removed.)
 *    Connect and Disconnect need `integrations.manage`, so a viewer
 *    sees the button disabled with the server's refusal copy as its
 *    title instead of learning on click. A connection in `error`
 *    offers Reconnect (OAuth) beside Disconnect.
 *  - `StatusPill` — the verified-check + bordered tag rendered next
 *    to the integration name in the hero.
 *  - `ConnectionNotices` — the one-line reasons under the hero
 *    description: why a connection needs attention, and Stripe test
 *    mode.
 *  - `PROVIDER_CATEGORY` — the category map the `MetaRail` Category
 *    row reads, as catalog keys (`integrations.categories`).
 *
 * The file name is kept for now to minimise churn in unrelated imports.
 */

/** Each data direction's key in the `integrations` catalog (`directions`). */
export const DIRECTION_KEY = {
  data_in: 'dataIn',
  data_out: 'dataOut',
} as const satisfies Record<IntegrationMeta['dataDirection'], string>

/** Each provider's category, as its key in the `integrations` catalog (`categories`). */
export const PROVIDER_CATEGORY = {
  loops: 'emailMarketing',
  braze: 'marketingAutomation',
  brevo: 'marketingAutomation',
  clerk: 'authentication',
  customerio: 'marketingAutomation',
  iterable: 'marketingAutomation',
  klaviyo: 'marketingAutomation',
  mailchimp: 'marketingAutomation',
  hubspot: 'crm',
  mailgun: 'emailDelivery',
  mailjet: 'emailDelivery',
  postmark: 'emailDelivery',
  sendgrid: 'emailDelivery',
  onesignal: 'pushNotifications',
  stripe: 'payments',
  figma: 'design',
  typeform: 'forms',
  framer: 'forms',
  slack: 'chat',
  stytch: 'authentication',
  supabase: 'database',
  workos: 'authentication',
  shopify: 'commerce',
  revenuecat: 'subscriptions',
} as const satisfies Record<IntegrationProvider, string>

/**
 * Status indicator for the integration hero. Modeled on the
 * "verified-checkmark + tag" pattern used by app marketplaces (Attio,
 * Slack App Directory): a small green verified circle when the
 * integration is connected, paired with a bordered pill that names
 * the state. Available + coming-soon render as just the pill (no
 * verified circle).
 */
export function StatusPill({
  kind,
  className,
}: {
  kind:
    | 'connected'
    | 'pending_verification'
    | 'error'
    | 'available'
    | 'coming_soon'
  className?: string
}) {
  const t = useTranslations('integrations')
  if (kind === 'connected') {
    return (
      <span className={cn('flex items-center gap-1.5', className)}>
        <span
          className="flex size-4 items-center justify-center rounded-lg bg-emerald-500"
          aria-hidden
        >
          <Check className="size-2.5 text-white" strokeWidth={3.5} />
        </span>
        <span className="flex h-5 items-center rounded-sm border border-emerald-500/40 bg-emerald-500/8 px-1.5 font-medium text-caption text-emerald-700 dark:text-emerald-400">
          {t('status.connected')}
        </span>
      </span>
    )
  }
  if (kind === 'coming_soon') {
    return (
      <span
        className={cn(
          'flex h-5 items-center rounded-sm border border-amber-500/40 bg-amber-500/10 px-1.5 font-medium text-amber-700 text-caption dark:text-amber-400',
          className
        )}
      >
        {t('status.comingSoon')}
      </span>
    )
  }
  if (kind === 'pending_verification') {
    return (
      <span
        className={cn(
          'flex h-5 items-center rounded-sm border border-amber-500/40 bg-amber-500/10 px-1.5 font-medium text-amber-700 text-caption dark:text-amber-400',
          className
        )}
      >
        {t('status.pendingVerification')}
      </span>
    )
  }
  if (kind === 'error') {
    return (
      <span
        className={cn(
          'flex h-5 items-center rounded-sm border border-red-500/40 bg-red-500/10 px-1.5 font-medium text-caption text-red-700 dark:text-red-400',
          className
        )}
      >
        {t('status.error')}
      </span>
    )
  }
  return (
    <span
      className={cn(
        'flex h-5 items-center rounded-sm border border-border px-1.5 font-medium text-caption text-muted-foreground',
        className
      )}
    >
      {t('status.available')}
    </span>
  )
}

type NoticeTone = 'error' | 'warning'

const NOTICE_ICON_CLASS: Record<NoticeTone, string> = {
  error: 'text-red-600 dark:text-red-400',
  warning: 'text-amber-600 dark:text-amber-400',
}

function HeroNotice({
  tone,
  children,
}: {
  tone: NoticeTone
  children: ReactNode
}) {
  return (
    <p className={cn(TYPE.body, 'flex items-start gap-1.5 leading-snug')}>
      <AlertTriangle
        className={cn(ICON.md, 'mt-0.5 shrink-0', NOTICE_ICON_CLASS[tone])}
        aria-hidden
      />
      <span className="min-w-0 flex-1">{children}</span>
    </p>
  )
}

/**
 * The hero's one-line reasons, under the description: why a connection
 * needs attention (the reason its row carries — an OAuth refresh the
 * provider refused — or a plain fallback), why a disconnected row was taken
 * over (another brand connected the account), and a Stripe grant made in
 * test mode. Copy, not a toast: it stays until the connection is fixed.
 */
export function ConnectionNotices({
  hasConnectionError,
  connectionError,
  inactiveReason,
  isStripeTestMode,
}: {
  hasConnectionError: boolean
  connectionError: string | undefined
  /** A disconnected row's reason (another brand took the account). */
  inactiveReason?: string
  isStripeTestMode: boolean
}) {
  const t = useTranslations('integrations')
  if (!(hasConnectionError || inactiveReason || isStripeTestMode)) {
    return null
  }
  return (
    <div className="mt-2 flex flex-col gap-1">
      {hasConnectionError ? (
        <HeroNotice tone="error">
          {connectionError?.trim() || t('notices.connectionErrorFallback')}
        </HeroNotice>
      ) : null}
      {inactiveReason ? (
        <HeroNotice tone="warning">{inactiveReason}</HeroNotice>
      ) : null}
      {isStripeTestMode ? (
        <HeroNotice tone="warning">{t('notices.stripeTestMode')}</HeroNotice>
      ) : null}
    </div>
  )
}

function StripeConnectFailureLine() {
  const t = useTranslations('integrations')
  const failure = describeStripeConnectError(
    t,
    useSearchParams().get('stripe_error')
  )
  return failure ? (
    <div className="mt-2">
      <HeroNotice tone="error">{failure}</HeroNotice>
    </div>
  ) : null
}

/**
 * Why the Stripe install that just came back failed (`?stripe_error=`, set
 * by the OAuth callback). Its own Suspense boundary: reading the URL's
 * search params bails only this line, not the page body, out of the
 * statically prerendered shell.
 */
export function StripeConnectFailureNotice() {
  return (
    <Suspense fallback={null}>
      <StripeConnectFailureLine />
    </Suspense>
  )
}

interface CtaBlockProps {
  integration: IntegrationMeta
  isConnected: boolean
  isPendingVerification?: boolean
  /**
   * The connection row is in `error` (an OAuth refresh the provider
   * refused). The connect button reads "Reconnect <Provider>" for an
   * OAuth provider and runs the same flow; Disconnect sits beside it.
   */
  hasConnectionError?: boolean
  isConnecting: boolean
  isDisconnecting: boolean
  isOAuthProvider: boolean
  onConnectClick: () => void
  onDisconnectClick: () => void
  /**
   * Label of the connected-state button. Defaults to Disconnect; a
   * provider whose disconnect lives in its own settings (Slack) says
   * Manage and routes there instead.
   */
  connectedLabel?: string
  /**
   * Visual variant. The hero uses the default (`h-9`, `text-sm`)
   * for presence. The `'compact'` variant (`h-8`, `text-xs`) is a
   * slimmer button kept for reuse in tight strips.
   */
  size?: 'default' | 'compact'
  /**
   * Optional override that disables the connect button regardless of
   * the in-flight `isConnecting` state. Used by the integration
   * detail page to hold an API-key Connect button disabled until the
   * inline tutorial form is fully valid — keeps the CTA in a single
   * stable location instead of duplicating it at the bottom of the
   * form.
   */
  isDisabled?: boolean
  className?: string
}

export function CtaBlock(props: CtaBlockProps) {
  const t = useTranslations('integrations')
  const manageGate = useCapabilityGate('integrations.manage')
  const {
    integration,
    isConnected,
    isPendingVerification,
    hasConnectionError = false,
    isConnecting,
    isDisconnecting,
    isOAuthProvider,
    onConnectClick,
    onDisconnectClick,
    connectedLabel = t('cta.disconnect'),
    size = 'default',
    isDisabled,
    className,
  } = props

  // Subtle, settings-style buttons — not the fully-rounded marketing
  // pill we used previously. Matches the SaaS settings-page reference
  // (small, `rounded-lg`, modest padding) so the CTA reads as a
  // utility action rather than a hero call-to-action.
  const sizeClasses =
    size === 'compact'
      ? 'h-8 rounded-lg px-3 text-xs'
      : 'h-9 rounded-lg px-4 text-sm'

  if (integration.availability === 'coming_soon') {
    return (
      <Button
        disabled
        variant="secondary"
        className={cn('font-medium', sizeClasses, className)}
      >
        {t('status.comingSoon')}
      </Button>
    )
  }

  if (isConnected || isPendingVerification) {
    return (
      <DisconnectButton
        label={connectedLabel}
        isDisconnecting={isDisconnecting}
        isAllowed={manageGate.isAllowed}
        deniedReason={manageGate.reason}
        onClick={onDisconnectClick}
        className={cn('font-medium', sizeClasses, className)}
      />
    )
  }

  const isConnectDisabled =
    isConnecting || isDisabled === true || !manageGate.isAllowed
  const connectLabel = (
    <ConnectButtonLabel
      isConnecting={isConnecting}
      isOAuthProvider={isOAuthProvider}
      reconnectName={hasConnectionError ? integration.name : undefined}
    />
  )

  // A connection in `error` still has a row on the brand: offer the fix
  // (the same connect flow) and keep Disconnect beside it.
  if (hasConnectionError) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <DisconnectButton
          label={t('cta.disconnect')}
          isDisconnecting={isDisconnecting}
          isAllowed={manageGate.isAllowed}
          deniedReason={manageGate.reason}
          onClick={onDisconnectClick}
          className={cn('font-medium', sizeClasses)}
        />
        <Button
          className={cn('font-medium', sizeClasses)}
          onClick={onConnectClick}
          disabled={isConnectDisabled || isDisconnecting}
          title={manageGate.reason ?? undefined}
        >
          {connectLabel}
        </Button>
      </div>
    )
  }

  return (
    <Button
      className={cn('font-medium', sizeClasses, className)}
      onClick={onConnectClick}
      disabled={isConnectDisabled}
      title={manageGate.reason ?? undefined}
    >
      {connectLabel}
    </Button>
  )
}

function DisconnectButton({
  label,
  isDisconnecting,
  isAllowed,
  deniedReason,
  onClick,
  className,
}: {
  label: string
  isDisconnecting: boolean
  isAllowed: boolean
  deniedReason: string | null
  onClick: () => void
  className: string
}) {
  const t = useTranslations('integrations')
  return (
    <Button
      variant="outline"
      className={className}
      onClick={onClick}
      disabled={isDisconnecting || !isAllowed}
      title={deniedReason ?? undefined}
    >
      {isDisconnecting ? (
        <>
          <Loader2 className="mr-1.5 size-3.5 animate-spin" aria-hidden />
          {t('cta.disconnecting')}
        </>
      ) : (
        label
      )}
    </Button>
  )
}

function ConnectButtonLabel({
  isConnecting,
  isOAuthProvider,
  reconnectName,
}: {
  isConnecting: boolean
  isOAuthProvider: boolean
  /** Set while the connection is in `error`: OAuth reads "Reconnect <name>". */
  reconnectName?: string
}) {
  const t = useTranslations('integrations')
  if (isConnecting) {
    return (
      <>
        <Loader2 className="mr-1.5 size-3.5 animate-spin" aria-hidden />
        {t('cta.connecting')}
      </>
    )
  }
  if (isOAuthProvider) {
    return (
      <>
        <ExternalLink className="mr-1.5 size-3.5" aria-hidden />
        {reconnectName
          ? t('cta.reconnect', { name: reconnectName })
          : t('cta.installOAuth')}
      </>
    )
  }
  return <>{t('cta.connect')}</>
}
